<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>
      <div class="element-box">
        <CoachingOverviewData :coaching="coachingData"/>
        <Menu :coaching="coachingData"
              :currentUser="piniaCurrentUser"/>

        <div class="row text-right">
          <div class="col-12">
            <button type="button" class="btn btn-primary" @click="handleProtocolModalForm">Protokoll hinzufügen
            </button>
            <button type="button" class="btn btn-outline-primary" @click="handleNoteModalForm">Zwischennotiz
            </button>
          </div>
        </div>

        <div class="table-responsive overflow-auto mt-5">
          <table class="table table-striped table-hover">
            <thead>
            <tr>
              <th style="font-size: 12px;">Termin</th>
              <th style="font-size: 12px;">Startzeit</th>
              <th style="font-size: 12px;">Endzeit</th>
              <th style="font-size: 12px;">Anwesenheit</th>
              <th style="font-size: 12px;">Sitzungsthema</th>
              <th class="text-right" style="font-size: 12px;">Einheiten</th>
              <th class="text-center" style="font-size: 12px;">Unterschrift</th>
              <th class="text-center" style="font-size: 12px;">Hausbesuch</th>
              <th style="font-size: 12px;">Coachart</th>
              <th style="font-size: 12px;">Coach</th>
              <th style="font-size: 12px;">Beschreibung</th>
              <th style="font-size: 12px;" v-if="currentUserRole === 'ROLE_ADMIN'"></th>
              <th :style="isDigitalSignature
                    ? 'min-width:200px; text-align: right; font-size: 12px; padding-right: 50px;'
                    : 'min-width:100px; text-align: right; font-size: 12px; padding-right: 50px;'">
                Aktion
              </th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="protocol in protocolList"
                :key="protocol.id"
                :class="['table-row', { 'bg-light-gray': protocol.note }]"
                @click="handleClickOnEdit(protocol)">
              <template v-if="!protocol.note">
                <th scope="row">{{ protocol?.termin }}</th>
                <td>{{ protocol?.startTime }}</td>
                <td>{{ protocol?.endTime }}</td>
                <td>{{ protocol?.presenceTranslation }}</td>
                <td>{{ truncate(protocol?.meetingTopic, 30) }}</td>
                <td class="text-right">{{ protocol?.units }}</td>
                <td class="text-center">
                  <template v-if="protocol.groupCoaching.digitalSignature">
                    <template v-if="!protocol.digitalSignatureImage">
                      <span style="color: #cf1020">
                        Fehlende Digitale Unterschrift
                      </span>
                    </template>
                    <template v-else>
                      JA
                    </template>
                  </template>
                  <template v-else>
                    <template v-if="protocol.signature">
                      JA
                    </template>
                    <template v-else>
                      NEIN
                    </template>
                  </template>
                </td>
                <td class="text-center">
                  {{ protocol.homeVisit ? 'JA' : 'NEIN' }}
                </td>
                <td>{{ protocol?.coachTypeTranslation }}</td>
                <td>{{ protocol?.coach?.fullName }}</td>
                <td>{{ truncate(protocol?.description, 60) }}</td>
              </template>
              <template v-else>
                <th scope="row">{{ protocol?.termin }}</th>
                <td colspan="10">
                  {{ protocol?.createdByUserData?.fullName }}
                  <span style="margin: 0 15px;">|</span>
                  {{ truncate(protocol?.description, 180) }}
                </td>
              </template>

              <td class="text-right" v-if="currentUserRole === 'ROLE_ADMIN'">
                <button class="btn btn-primary"
                        v-if="protocol.updatedByAdmin"
                        @click.stop="handleMeetingHistoryModal(protocol?.id)">
                  <i class="os-icon os-icon-rotate-ccw"></i>
                </button>
              </td>

              <td class="text-center">
                <div class="d-flex justify-content-end">
                  <div v-if="!protocol.note && isDigitalSignature" style="margin-right: 30px; width: max-content">
                    <button class="btn btn-primary" @click.stop="handleDigitalSignatureModal(protocol?.id)">
                      Digitale Unterschrift
                    </button>
                  </div>
                  <div v-if="!protocol.invoiced && !protocol.coachInvoiced"
                       style="width: 40px;">
                    <button class="btn btn-danger" @click.stop="handleDeleteModal(protocol?.id)">
                      <i class="os-icon os-icon-trash"></i>
                    </button>
                  </div>
                  <div v-else style="float: right">
                    <div style="width: 40px"></div>
                  </div>
                </div>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </PageTemplate>

  <Modal
      v-model="showProtocolModalForm"
      title="Protokoll"
      :hide-cancel="true"
      :button-label="$t('general.button.save')"
      :customWidth="'1400px'"
      :hide-footer="true"
      classFooter="justify-content-center">
    <Form :protocolId="protocolId"
          :coachingId="coachingId"
          :operationType="operationType"
          :isDigitalSignature="coachingData?.requiresSignature"
          v-model:submitForm="submitForm"/>
  </Modal>

  <Modal
      v-model="showNoteModalForm"
      title="Zwischennotiz"
      :hide-cancel="true"
      :button-label="$t('general.button.save')"
      sizeModal="modal-xl"
      :hide-footer="true"
      classFooter="justify-content-center">
    <NoteForm :protocolId="protocolId"
              :coachingId="coachingId"
              v-model:submitForm="submitForm"/>
  </Modal>

  <Modal
      v-model="showDigitalSignatureModal"
      title="Digitale Unterschrift"
      :hide-cancel="true"
      :button-label="$t('general.button.save')"
      sizeModal="modal-md"
      :hide-footer="true"
      classFooter="justify-content-center">
    <GroupCoachingDigitalSignatureForm
        :meetingId="protocolId"
        :coachingId="coachingId"
        meetingType="protocol"
        v-model:submitForm="submitForm"/>
  </Modal>

  <ModalDelete
      v-model="showDeleteModal"
      :header="t('general.delete_item_header')"
      :onDelete="deleteProtocol">
  </ModalDelete>

  <Modal
      title="Begegnungsgeschichte"
      :button-label="$t('general.button.save')"
      :customWidth="'1400px'"
      v-model="showMeetingHistoryModal"
      :hide-footer="true"
      classFooter="justify-content-center">
    <MeetingHistoryList :meetingId="protocolId"/>
  </Modal>
</template>

<script setup>
import portalGroupCoachingService from '@/api/Portal/GroupCoaching';
import unemployedGroupCoachingService from '@/api/Unemployed/GroupCoaching';
import Loader from '@/components/Loader.vue';
import Modal from '@/components/modal/Modal.vue';
import ModalDelete from '@/components/modal/ModalDelete.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import {CloseToast, ErrorToast, SuccessToast} from '@/components/VueToast';
import {piniaUserStore} from '@/store/piniaUserStore';
import CoachingOverviewData from '@/views/App/Portal/GroupCoaching/CoachingOverviewData.vue';
import GroupCoachingDigitalSignatureForm from '@/views/App/Portal/GroupCoaching/GroupCoachingDigitalSignatureForm.vue';
import Menu from '@/views/App/Portal/GroupCoaching/Menu.vue';
import Form from '@/views/App/Portal/GroupCoaching/Protocol/Form.vue';
import NoteForm from '@/views/App/Portal/GroupCoaching/Protocol/NoteForm.vue';
import {computed, onMounted, ref, watch} from 'vue';
import {useI18n} from 'vue-i18n';
import {useRoute} from 'vue-router';
import MeetingHistoryList from "@/views/App/Portal/GroupCoaching/MeetingHistoryList.vue";

const piniaUserState = piniaUserStore();
const piniaCurrentUser = computed(() => piniaUserState.currentUser.user);
const currentUserRole = piniaCurrentUser?.value?.roles[0];
const {t} = useI18n();
const route = useRoute();
const coachingData = ref('');
const operationType = ref('');
const protocolId = ref(null);
const protocolList = ref([]);
const coachingId = +route.params.id;
const isLoaded = ref(false);
const isDigitalSignature = ref(false);
const showProtocolModalForm = ref(false);
const showNoteModalForm = ref(false);
const showDeleteModal = ref(false);
const showDigitalSignatureModal = ref(false);
const submitForm = ref(false);
const showMeetingHistoryModal = ref(false)

watch(submitForm, () => {
  protocolId.value = null;
  showProtocolModalForm.value = false;
  showNoteModalForm.value = false;
  showDeleteModal.value = false;
  showDigitalSignatureModal.value = false;
  getGroupCoachingData();
  getProtocolList();
});

async function getProtocolList() {
  try {
    const {data} = await portalGroupCoachingService.getProtocolList(coachingId);
    protocolList.value = data.data;
  } catch (error) {
    await ErrorToast(error);
  }
}

async function getGroupCoachingData() {
  try {
    CloseToast();
    const {data} = await unemployedGroupCoachingService.getGroupCoaching(+coachingId);

    coachingData.value = data.data;
    operationType.value = data?.data?.nameOfCoaching?.operationType;
    isDigitalSignature.value = data.data.digitalSignature;
  } catch (error) {
    await ErrorToast(error);
  }
}

function handleClickOnEdit(protocol) {
  CloseToast();
  if (protocol.note) {
    handleEditNoteModalForm(protocol.id);
  } else {
    handleEditProtocolModalForm(protocol.id);
  }
}

function handleProtocolModalForm() {
  CloseToast();
  protocolId.value = '';
  showProtocolModalForm.value = true;
  showNoteModalForm.value = false;
  showDeleteModal.value = false;
  showMeetingHistoryModal.value = false;
}

function handleEditProtocolModalForm(id) {
  CloseToast();
  protocolId.value = id;
  showProtocolModalForm.value = true;
  showNoteModalForm.value = false;
  showDeleteModal.value = false;
  showMeetingHistoryModal.value = false;
}

function handleNoteModalForm() {
  CloseToast();
  protocolId.value = '';
  showNoteModalForm.value = true;
  showProtocolModalForm.value = false;
  showDeleteModal.value = false;
  showMeetingHistoryModal.value = false;
}

function handleEditNoteModalForm(id) {
  CloseToast();
  protocolId.value = id;
  showNoteModalForm.value = true;
  showProtocolModalForm.value = false;
  showDeleteModal.value = false;
  showMeetingHistoryModal.value = false;
}

function handleDeleteModal(id) {
  CloseToast();
  protocolId.value = id;
  showDeleteModal.value = true;
  showProtocolModalForm.value = false;
  showNoteModalForm.value = false;
  showMeetingHistoryModal.value = false;
}

function handleDigitalSignatureModal(id) {
  CloseToast();
  protocolId.value = id;
  showDigitalSignatureModal.value = true;
  showDeleteModal.value = false;
  showProtocolModalForm.value = false;
  showNoteModalForm.value = false;
  showMeetingHistoryModal.value = false;
}

function handleMeetingHistoryModal(id) {
  CloseToast();
  protocolId.value = id;
  showMeetingHistoryModal.value = true;
  showDigitalSignatureModal.value = false;
  showDeleteModal.value = false;
  showProtocolModalForm.value = false;
  showNoteModalForm.value = false;
}

const truncate = (text, length) => {
  if (!text || typeof text !== 'string') {
    return ''; // Return an empty string if text is null, undefined, or not a string
  }

  const strippedText = text.replace(/<\/?[^>]+(>|$)/g, ''); // Remove HTML tags
  return strippedText.length > length ? strippedText.substring(0, length) + '...' : strippedText;
};

async function deleteProtocol() {
  try {
    await portalGroupCoachingService.protocolDelete(protocolId.value);

    protocolId.value = null;
    showDeleteModal.value = false;

    await getGroupCoachingData();
    await getProtocolList();

    SuccessToast(t('general.toasts.delete_success'));
  } catch (error) {
    await ErrorToast(error);
  }
}

onMounted(async () => {
  CloseToast()
  await Promise.all([
    getGroupCoachingData(),
    getProtocolList(),
  ]);
  isLoaded.value = true;
});
</script>

<style scoped>

</style>